import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const AchievementShowcase = ({ achievements, onShare }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const mockAchievements = [
    {
      id: 1,
      title: "First Steps",
      description: "Completed your first wellness goal",
      category: "milestones",
      icon: "Trophy",
      color: "bg-accent",
      earnedDate: "2025-09-15",
      rarity: "common",
      points: 100
    },
    {
      id: 2,
      title: "Community Helper",
      description: "Helped 10 community members with advice",
      category: "community",
      icon: "Users",
      color: "bg-primary",
      earnedDate: "2025-09-20",
      rarity: "uncommon",
      points: 250
    },
    {
      id: 3,
      title: "Wellness Warrior",
      description: "Maintained daily wellness routine for 30 days",
      category: "consistency",
      icon: "Shield",
      color: "bg-success",
      earnedDate: "2025-09-28",
      rarity: "rare",
      points: 500
    },
    {
      id: 4,
      title: "Knowledge Seeker",
      description: "Completed 5 educational workshops",
      category: "learning",
      icon: "BookOpen",
      color: "bg-secondary",
      earnedDate: "2025-10-02",
      rarity: "uncommon",
      points: 300
    },
    {
      id: 5,
      title: "Transformation Champion",
      description: "Achieved major wellness transformation",
      category: "milestones",
      icon: "Star",
      color: "bg-gradient-to-r from-accent to-primary",
      earnedDate: "2025-10-05",
      rarity: "legendary",
      points: 1000
    },
    {
      id: 6,
      title: "Event Enthusiast",
      description: "Attended 10 wellness events",
      category: "participation",
      icon: "Calendar",
      color: "bg-warning",
      earnedDate: "2025-10-07",
      rarity: "common",
      points: 200
    }
  ];

  const categories = [
    { value: 'all', label: 'All Achievements', icon: 'Award' },
    { value: 'milestones', label: 'Milestones', icon: 'Trophy' },
    { value: 'community', label: 'Community', icon: 'Users' },
    { value: 'consistency', label: 'Consistency', icon: 'Target' },
    { value: 'learning', label: 'Learning', icon: 'BookOpen' },
    { value: 'participation', label: 'Participation', icon: 'Calendar' }
  ];

  const filteredAchievements = selectedCategory === 'all' 
    ? mockAchievements 
    : mockAchievements?.filter(achievement => achievement?.category === selectedCategory);

  const totalPoints = mockAchievements?.reduce((sum, achievement) => sum + achievement?.points, 0);
  const rarityCount = mockAchievements?.reduce((acc, achievement) => {
    acc[achievement.rarity] = (acc?.[achievement?.rarity] || 0) + 1;
    return acc;
  }, {});

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return 'text-muted-foreground';
      case 'uncommon': return 'text-primary';
      case 'rare': return 'text-success';
      case 'legendary': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  const handleShare = (achievement) => {
    onShare?.(achievement);
    // Mock sharing functionality
    navigator.clipboard?.writeText(
      `I just earned the "${achievement?.title}" achievement on Digital Wellness Bank! ${achievement?.description}`
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <Icon name="Award" size={24} className="text-primary" />
        <h2 className="text-xl font-heading font-semibold text-foreground">Achievement Showcase</h2>
      </div>
      {/* Achievement Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 bg-primary/10 rounded-lg">
          <div className="text-2xl font-bold text-primary">{mockAchievements?.length}</div>
          <div className="text-sm text-muted-foreground">Total Earned</div>
        </div>
        <div className="text-center p-4 bg-accent/10 rounded-lg">
          <div className="text-2xl font-bold text-accent">{totalPoints?.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">Points Earned</div>
        </div>
        <div className="text-center p-4 bg-success/10 rounded-lg">
          <div className="text-2xl font-bold text-success">{rarityCount?.rare || 0}</div>
          <div className="text-sm text-muted-foreground">Rare Badges</div>
        </div>
        <div className="text-center p-4 bg-secondary/10 rounded-lg">
          <div className="text-2xl font-bold text-secondary">{rarityCount?.legendary || 0}</div>
          <div className="text-sm text-muted-foreground">Legendary</div>
        </div>
      </div>
      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories?.map((category) => (
          <button
            key={category?.value}
            onClick={() => setSelectedCategory(category?.value)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-wellness ${
              selectedCategory === category?.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name={category?.icon} size={16} />
            <span>{category?.label}</span>
          </button>
        ))}
      </div>
      {/* Achievements Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {filteredAchievements?.map((achievement) => (
          <div
            key={achievement?.id}
            className="border border-border rounded-lg p-4 hover:shadow-wellness transition-wellness"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-12 h-12 ${achievement?.color} rounded-lg flex items-center justify-center text-white`}>
                <Icon name={achievement?.icon} size={24} />
              </div>
              <div className="flex items-center space-x-2">
                <span className={`text-xs font-medium capitalize ${getRarityColor(achievement?.rarity)}`}>
                  {achievement?.rarity}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleShare(achievement)}
                  className="h-8 w-8"
                >
                  <Icon name="Share2" size={16} />
                </Button>
              </div>
            </div>
            
            <h3 className="font-heading font-medium text-foreground mb-2">{achievement?.title}</h3>
            <p className="text-sm text-muted-foreground mb-3">{achievement?.description}</p>
            
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Earned {new Date(achievement.earnedDate)?.toLocaleDateString()}</span>
              <div className="flex items-center space-x-1">
                <Icon name="Zap" size={12} className="text-accent" />
                <span>{achievement?.points} pts</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Recent Achievements */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">Recent Achievements</h3>
        <div className="space-y-3">
          {mockAchievements?.slice(0, 3)?.map((achievement) => (
            <div key={achievement?.id} className="flex items-center space-x-4 p-3 bg-muted/50 rounded-lg">
              <div className={`w-10 h-10 ${achievement?.color} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                <Icon name={achievement?.icon} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground truncate">{achievement?.title}</h4>
                <p className="text-sm text-muted-foreground">{achievement?.description}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-medium text-accent">+{achievement?.points}</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(achievement.earnedDate)?.toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Achievement Progress */}
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">Progress Towards Next Achievement</h3>
        <div className="space-y-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-foreground">Social Butterfly</h4>
              <span className="text-sm text-muted-foreground">7/10 interactions</span>
            </div>
            <div className="w-full bg-border rounded-full h-2 mb-2">
              <div className="bg-primary h-2 rounded-full" style={{ width: '70%' }}></div>
            </div>
            <p className="text-sm text-muted-foreground">Help 3 more community members to unlock this achievement</p>
          </div>
          
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-foreground">Consistency Master</h4>
              <span className="text-sm text-muted-foreground">45/60 days</span>
            </div>
            <div className="w-full bg-border rounded-full h-2 mb-2">
              <div className="bg-success h-2 rounded-full" style={{ width: '75%' }}></div>
            </div>
            <p className="text-sm text-muted-foreground">Maintain your routine for 15 more days</p>
          </div>
        </div>
      </div>
      {/* Share Profile */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-heading font-medium text-foreground">Share Your Achievements</h3>
            <p className="text-sm text-muted-foreground">Show off your wellness journey progress</p>
          </div>
          <Button
            variant="default"
            onClick={() => handleShare({ title: 'My Wellness Journey', description: `I've earned ${mockAchievements?.length} achievements and ${totalPoints} points!` })}
            iconName="Share2"
            iconPosition="left"
          >
            Share Profile
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AchievementShowcase;